import {
  put,
  call,
  select,
  all,
  fork,
  cancelled,
  delay,
} from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import {
  PapiRequestDaDeviationInit,
  PapiDaDeviationInit,
  PapiDeviation,
} from '@wix/da-papi-types';
import { initDeviationPage } from '../../DeviationPage/redux/actions';
import { DeviationResourceType } from '../../types/deviation';
import { requestPuppy } from '@wix/da-http-client';
import {
  getCurrentBiMetadata,
  getItemIdsForPreload,
  getPreloadingEnabled,
} from './selectors';
import { loadDuperbrowseItem } from './actionCreators';
import {
  getExtendedDeviationById,
  getDeviationIdFromNormalizedId,
} from '../../redux/deviations/selectors';
import { getCommentCursorFromQuery } from '../../CommentThread/helpers';
import history from '../../redux/routing/history';
import { DuperbrowseItemSchema } from './types';
import { isExtendedDeviationFullyLoaded } from '../../DeviationPage/redux/helpers';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import { getBiEventData } from '../../DeviationPage/redux/selectors';
import { logPageView } from '../../biLogger/redux/actions';

const logger = ddt.createLogger('duperbrowse');

export function getDeviationPageTypeFromTypeId(typeId: number) {
  switch (typeId) {
    case DeviationResourceType.JOURNAL:
      return 'journal';
    case DeviationResourceType.COMMISSION:
      return 'commission';
    case DeviationResourceType.POLL:
      return 'poll';
    case DeviationResourceType.STATUS:
      return 'status-update';
    case DeviationResourceType.TIER:
      // TODO member_subscriptions clean up any when backend supports tiers for related content
      return 'tier' as any;
    default:
      return 'art';
  }
}

function* loadDeviation(
  itemId: number | string,
  preload: boolean,
  currentDeviationId?: number | string
) {
  // check if we already have fetched data for extended
  const deviation: PapiDeviation = yield select(
    getExtendedDeviationById,
    itemId
  );
  if (!deviation) {
    logger.error('Deviation not found', itemId);
    return false;
  }

  if (isExtendedDeviationFullyLoaded(deviation)) {
    // already loaded, we good
    logger.log('Deviation already loaded, bailing', itemId);
    return false;
  }

  logger.log('Loading deviation', itemId, { preload, currentDeviationId });

  try {
    const csrf = yield select(getConfigKey, 'csrfToken');
    const queryParams = new URLSearchParams(history.location.search);
    const parsedQuery = {};
    queryParams.forEach((value, key) => (parsedQuery[key] = value));
    const params: PapiRequestDaDeviationInit & {
      csrf_token: string;
      expand: string;
    } = {
      deviationid: !deviation.isPublished
        ? (deviation.stashPrivateid ?? 0)
        : deviation.deviationId,
      username: (deviation.author && deviation.author.username) ?? '',
      type: getDeviationPageTypeFromTypeId(deviation.typeId),
      include_session: false,
      comments_cursor: getCommentCursorFromQuery(parsedQuery, {
        itemId: deviation.deviationId,
      }),
      csrf_token: csrf,
      expand: 'deviation.related',
    };

    if (preload) {
      params.preload = true;
      if (currentDeviationId) {
        params.current_id = yield select(
          getDeviationIdFromNormalizedId,
          currentDeviationId
        );
      }
    }

    const result: PapiDaDeviationInit | false = yield call(
      requestPuppy,
      {
        method: 'get',
        url: !deviation.isPublished ? '/stash/init' : '/init',
        params: {
          ...params,
          da_minor_version: 20230710,
        },
      },
      () => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line
          const chance = require('chance').Chance();
          // eslint-disable-next-line
          const { Fake, commentThreads } = require('@wix/da-sample-data');
          return {
            gruser: {
              gruserId: 15691842,
              gruserTypeId: 1,
              gruserItemId: 292582046,
            },
            deviation: Fake.extendDeviation(deviation, {}),
            comments: {
              ...chance.pickone(commentThreads),
              commentableItemid: deviation.deviationId,
              commentableTypeid: deviation.typeId,
            },
          };
        }
        return false;
      },
      'dadeviation'
    );

    if (!result || !result.deviation) {
      return false;
    }

    yield put(
      initDeviationPage(
        result.deviation,
        result.comments,
        result.pageBiMetadata,
        result['gruser']
      )
    );
    return true;
  } finally {
    if (yield cancelled()) {
      logger.log('Loading canceled', deviation, { preload });
    }
  }
}

function getLoader(itemSchema: DuperbrowseItemSchema) {
  switch (itemSchema) {
    case 'deviation':
      return loadDeviation;
    default:
  }
}

export function* handleLoading({
  payload: { itemId, itemSchema, preloadDirection },
}: ReturnType<typeof loadDuperbrowseItem>) {
  yield delay(200); // debounce

  const loader = getLoader(itemSchema);
  if (!loader) {
    logger.log('No loader found', itemSchema);
    return;
  }

  logger.log('Starting main load', itemId);
  // this is where we load the current item synchronously
  const wasRealLoad = yield call(loader, itemId, false);
  const currentItem = wasRealLoad ? undefined : itemId; // we only should pass currentItem for preloaded deviations

  if (itemSchema === 'deviation') {
    // We track BI here as we want a "full" pageload before sending the event
    // and this handler best suits that logic
    const deviation = yield select(getExtendedDeviationById, itemId);
    const biMetadata = yield select(getCurrentBiMetadata, itemId);
    const {
      view,
      eventComponent,
      deviationId,
      deviationResourceType,
      isDuperbrowse,
      contentViewStatus,
    } = yield select(getBiEventData, deviation);

    yield put(
      logPageView({
        view,
        component: eventComponent,
        typeid: deviationResourceType,
        itemid: deviationId,
        is_duper: isDuperbrowse ? 1 : 0,
        content_view_status: contentViewStatus,
        devpageLoadingPhase: 'phase1-5',
        metadata: biMetadata,
      })
    );
  }

  const preloadEnabled = yield select(getPreloadingEnabled);

  if (preloadEnabled) {
    const navigation: ReturnType<typeof getItemIdsForPreload> = yield select(
      getItemIdsForPreload,
      preloadDirection
    );

    const preloadEffects = navigation.map(item =>
      fork(loader, item, true, currentItem)
    );

    logger.log('Forking preloads', navigation);
    // here we fork out the preloaders to do their thing async
    yield all(preloadEffects);
  } else {
    logger.log('No preloading for this stream');
  }
}
